import { isPlatformBrowser } from '@angular/common';
import { Component, effect, inject, OnDestroy, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

import { CalendarEntryComponent } from '@components/calendar-entry/calendar-entry.component';
import { FooterComponent } from "@components/footer/footer.component";
import { ThemeSwitcherComponent } from '@components/theme-switcher/theme-switcher.component';
import { SiteHeaderComponent } from "@components/site-header/site-header.component";

import { calendarEasterEggs } from "@data/pixel-map/pixel-map-calendar";

import IPixelMapDay from '@interfaces/pixel-map-day.interface';

import { CalendarDataService } from '@services/calendar-data.service';
import { MetaService } from '@services/meta.service';

import { themeColors } from '@utils/journal-colors.config';

@Component({
	selector: 'app-calendar-day',
	templateUrl: './calendar-day.component.html',
	styleUrls: ['./calendar-day.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CalendarEntryComponent, ThemeSwitcherComponent, SiteHeaderComponent, SiteHeaderComponent, FooterComponent]
})
export class CalendarDayComponent implements OnDestroy {
	private route = inject(ActivatedRoute);
	private calendarDataService = inject(CalendarDataService);
	private router = inject(Router);
	private meta = inject(Meta);
	private metaService = inject(MetaService);
	private titleService = inject(Title);

	isEasterEggEnabled = false;
	isDateValid = false;
	dateData: IPixelMapDay | undefined;

	private platformId = inject<Object>(PLATFORM_ID);

	calendar: any;
	calendarMonths = this.calendarDataService.calendarMonths;

	year = 2023;
	month = 0;
	day = 0;

	constructor() {
		const routeDate = toSignal(
			this.route.params.pipe(
				map(params => ({
					year: +params['year'],
					month: +params['month'] - 1,
					day: +params['day'] - 1,
				})),
			),
			{ initialValue: { year: this.year, month: this.month, day: this.day } },
		);

		effect(() => {
			const { year, month, day } = routeDate();
			const months = this.calendarDataService.adjustFebruaryDays(year);

			if (this.isValidDate(year, month, day, months)) {
				this.isDateValid = true;
				const mm = String(month + 1).padStart(2, "0");
				const dd = String(day + 1).padStart(2, "0");
				this.metaService.updateCanonicalUrl(`/calendar/${year}/${mm}/${dd}`);

				this.calendar = {
					days: Array(31).fill(1).map((x, i) => i),
					months,
					data: []
				};

				this.fetchDateData(year, month, day);
			} else {
				console.error("Invalid date");
				this.isDateValid = false;
				this.metaService.updateCanonicalUrl();
				this.triggerInvalidDateEasterEgg();
			}
		});
	}

	ngOnDestroy(): void {
		this.metaService.restoreOriginalSiteInfo();
	}

	// Validation logic
	isValidDate(year: number, month: number, day: number, months: any[]): boolean {
		// Allow only 01/00 as valid
		if (month === 0 && day === -1) return true;

		// Validate other dates
		if (month < 0 || month >= months.length) return false; // Invalid month
		if (day < 0 || day >= months[month].days) return false; // Invalid day
		return true; // Date is valid
	}

	// Fetch data for the specific year/month/day
	fetchDateData(year: number, month: number, day: number) {
		let dayData = null;

		try {
			if (month === 0 && day === -1) {
				dayData = calendarEasterEggs[year - 1];
			} else {
				const monthData = this.calendarDataService.getMonthData(year, month);
				dayData = monthData?.[day]; // Use optional chaining to safely access the data
			}

			if (dayData) {
				// Process your date data here
				this.year = year;
				this.month = month;
				this.day = day;

				this.dateData = dayData;
				this.updateMeta(dayData);
				// console.log(this.dateData, "XXXX");
				// this.cdr.detectChanges();
			} else {
				console.warn(`No data found for the date: Year ${year}, Month ${month + 1}, Day ${day + 1}`);
				// Handle undefined case here, like showing a fallback message
				this.dateData = undefined;
				this.triggerInvalidDateEasterEgg();
				throw new Error(`No data found for the date: Year ${year}, Month ${month + 1}, Day ${day + 1}`);
				// this.router.navigate(["/"]);
			}
		} catch (error) {
			console.error("Error fetching date data:", error);
			this.triggerInvalidDateEasterEgg();
			// this.router.navigate(["/"]);
		}
	}

	async setActiveDate(month: number, date: number) {
		const m = String(month + 1).padStart(2, "0");
		const d = String(date + 1).padStart(2, "0");

		// if (this.calendar.data?.[month]?.[date]) {
		// this.fetchDateData(Number(this.year), month, date);
		// }
		this.router.navigate([`/calendar/${this.year}/${m}/${d}`]);

		this.month = month;
		this.day = date;
	}

	updateMeta(data: IPixelMapDay): void {
		const currentDate = `${this.calendarMonths[this.month].shortName} ${this.day + 1}, ${this.year}`;
		const day   = this.day + 1;
		const month = this.month + 1;
		const isoDateBase = `${this.year}-${month.toString().padStart(2,'0')}-${day.toString().padStart(2,'0')}`;
		
		// Manila is UTC+8: midnight plus 1 minute
  		const datePublished = `${isoDateBase}T00:01:00+08:00`;

		const screenshotUrl = this.generateScreenshotUrl();

		const canonicalPath = `/calendar/${this.year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
		const canonicalUrl = this.metaService.updateCanonicalUrl(canonicalPath);

		const rawEntryTitle = data.title?.trim() || '';
		const headline = (rawEntryTitle || currentDate).substring(0, 110);

		// Construct the page title (tabs / share previews). Google will truncate this anyway.
		const maxPageTitleLength = 60;
		const truncatedEntryTitle =
			rawEntryTitle.length > maxPageTitleLength
				? rawEntryTitle.slice(0, maxPageTitleLength - 1) + '…'
				: rawEntryTitle;
		const pageTitle = rawEntryTitle
			? `${truncatedEntryTitle} - ${currentDate} - Dartegnian.com`
			: `${currentDate} - Dartegnian.com`;

		const keywords = "journal entry, diary entry, mood calendar entry, mood calendar, journal, calendar day, Dartegnian's journal entry, Dartegnian Velarde's diary entry";
		let themeColor = "";

		const rawReasonMarkdown = data.reason?.trim() || `Journal entry for ${currentDate}. Mood: ${data.status}.`;
		const rawReason = this.markdownToPlainText(rawReasonMarkdown);

		// Create a short excerpt for SEO description (~150-160 chars)
		const excerptMaxLength = 155;
		let shortReason = rawReason;
		if (shortReason.length > excerptMaxLength) {
			shortReason = shortReason.slice(0, excerptMaxLength) + '...';
		}

		themeColor = themeColors[data.status];

		this.meta.updateTag({ name: 'description', content: shortReason });
		this.meta.updateTag({ name: 'keywords', content: keywords });
		this.meta.updateTag({ property: 'og:title', content: pageTitle });
		this.meta.updateTag({ property: 'og:description', content: shortReason });
		this.meta.updateTag({ property: 'og:type', content: 'article' });
		this.meta.updateTag({ property: 'article:published_time', content: datePublished });
		this.meta.updateTag({ property: 'article:modified_time', content: datePublished });
		this.meta.updateTag({ property: 'article:author', content: 'https://dartegnian.com' });
		this.meta.updateTag({ property: 'article:section', content: data.status });
		this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
		this.meta.updateTag({ name: 'twitter:description', content: shortReason });

		this.meta.updateTag({ property: 'og:image', content: screenshotUrl });
		this.meta.updateTag({ property: 'og:image:secure_url', content: screenshotUrl });
		this.meta.updateTag({ property: 'og:image:alt', content: `Screenshot of diary entry ` + pageTitle.replace(" - Dartegnian.com", " by Dartegnian Velarde") });
		this.meta.updateTag({ name: 'twitter:image', content: screenshotUrl });

		this.meta.updateTag({ property: 'og:image:width',     content: '1200' });
		this.meta.updateTag({ property: 'og:image:height',    content: '800' });

		// (optional) for Twitter “large” previews
		this.meta.updateTag({ name:     'twitter:card',       content: 'summary_large_image' });

		if (!isPlatformBrowser(this.platformId)) {
			this.metaService.updateSiteColorMeta(themeColor);
		}

		this.titleService.setTitle(pageTitle);
		this.updateStructuredData({
			url: canonicalUrl,
			datePublished,
			headline,
			pageTitle,
			excerpt: shortReason,     // pass truncated text
			fullText: rawReason,     // pass the untruncated text
			status: data.status,
			imageUrl: screenshotUrl,
			location: data.location,
			spotify: data.spotify
		});
	}

	private markdownToPlainText(markdown: string): string {
		return markdown
			.replace(/```[\s\S]*?```/g, (block) => block.replace(/```/g, '').trim())
			.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
			.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
			.replace(/(\*\*|__)(.*?)\1/g, '$2')
			.replace(/(\*|_)(.*?)\1/g, '$2')
			.replace(/~~(.*?)~~/g, '$1')
			.replace(/`([^`]+)`/g, '$1')
			.replace(/^>\s?/gm, '')
			.replace(/^#{1,6}\s+/gm, '')
			.replace(/\s+/g, ' ')
			.trim();
	}

	/**
	 * Insert or update JSON-LD script tag in head
	 */
	updateStructuredData(params: {
		url: string;
		datePublished: string;
		headline: string;
		pageTitle: string;
		excerpt: string;
		fullText: string;
		status: string;
		imageUrl: string;
		location?: IPixelMapDay['location'];
		spotify?: IPixelMapDay['spotify'];
	}): void {
		const webPageId = params.url;
		const blogPostingId = `${params.url}#blogposting`;
		const imageId = `${params.url}#primaryimage`;
		const breadcrumbId = `${params.url}#breadcrumb`;

		const wordCount = params.fullText.trim() ? params.fullText.trim().split(/\s+/).length : undefined;

		const keywords = [
			'journal entry',
			'diary entry',
			'mood calendar',
			params.status,
			params.location?.place,
			params.spotify?.song,
			params.spotify?.artist
		].filter(Boolean);

		const uniqueKeywords = Array.from(new Set(keywords.map(k => String(k).trim()).filter(Boolean)));

		const blogPosting: Record<string, any> = {
			'@type': ['Article', 'BlogPosting'],
			'@id': blogPostingId,
			'mainEntityOfPage': { '@type': 'WebPage', '@id': webPageId },
			'url': params.url,
			'headline': params.headline,
			'description': params.excerpt,
			'articleBody': params.fullText,
			'image': [
				params.imageUrl
			],
			'datePublished': params.datePublished,
			'dateModified': params.datePublished,
			'inLanguage': 'en',
			'isPartOf': {
				'@type': 'Blog',
				'name': 'Mood Calendar & Journal',
				'url': 'https://dartegnian.com/#mood-calendar',
				'description': 'A personal mood calendar with real daily journal entries, updated consistently since January 2023.'
			},
			'author': {
				'@type': 'Person',
				'name': 'Dartegnian Velarde',
				'url': 'https://dartegnian.com',
				'image': 'https://dartegnian.com/assets/img/me-min-512.jpg'
			},
			'publisher': {
				'@type': 'Organization',
				'name': 'Dartegnian.com',
				'logo': {
					'@type': 'ImageObject',
					'url': 'https://dartegnian.com/assets/icons/icon-192x192.png',
					'width': 192,
					'height': 192
				}
			},
			'keywords': uniqueKeywords,
			'articleSection': params.status
		};

		if (wordCount) {
			blogPosting['wordCount'] = wordCount;
		}

		if (params.location?.place) {
			blogPosting['contentLocation'] = {
				'@type': 'Place',
				'name': params.location.place
			};
		}

		if (params.spotify?.song && params.spotify?.artist && params.spotify?.link) {
			blogPosting['mentions'] = [
				{
					'@type': 'MusicRecording',
					'name': `${params.spotify.song} — ${params.spotify.artist}`,
					'url': params.spotify.link,
					'byArtist': { '@type': 'MusicGroup', 'name': params.spotify.artist },
					'inAlbum': params.spotify.album ? { '@type': 'MusicAlbum', 'name': params.spotify.album } : undefined
				}
			].map((item) => {
				if (!item.inAlbum) delete (item as any).inAlbum;
				return item;
			});
		}

		const jsonLdObject = {
			'@context': 'https://schema.org',
			'@graph': [
				{
					'@type': 'WebPage',
					'@id': webPageId,
					'url': params.url,
					'name': params.pageTitle,
					'description': params.excerpt,
					'inLanguage': 'en',
					'breadcrumb': { '@id': breadcrumbId }
				},
				{
					'@type': 'ImageObject',
					'@id': imageId,
					'url': params.imageUrl,
					'width': 1200,
					'height': 800
				},
				{
					'@type': 'BreadcrumbList',
					'@id': breadcrumbId,
					'itemListElement': [
						{
							'@type': 'ListItem',
							'position': 1,
							'name': 'Dartegnian.com',
							'item': 'https://dartegnian.com/'
						},
						{
							'@type': 'ListItem',
							'position': 2,
							'name': 'Mood Calendar & Journal',
							'item': 'https://dartegnian.com/#mood-calendar'
						},
						{
							'@type': 'ListItem',
							'position': 3,
							'name': params.headline,
							'item': params.url
						}
					]
				},
				blogPosting
			]
		};

		this.metaService.writeJsonLd(jsonLdObject);
	}

	generateScreenshotUrl(): string {
		// Base URL of your Angular app
		const baseUrl = 'https://dartegnian.com';
		// Get the current route (without query params or fragments)
		const currentPath = this.router.url.split('#')[0].split('?')[0];
		const absoluteUrl = baseUrl + currentPath;
		// const absoluteUrl = 'https://google.com';
	  
		// Build a parameter object for the screenshot service.
		// Using JPEG format with lower quality and smaller viewport dimensions to reduce file size.
		const params: { [key: string]: any } = {
			url: absoluteUrl,
		//   format: 'jpeg',           // Use JPEG for better compression
		//   quality: 60,             // Lower quality to reduce file size (adjust as needed)
		//   'viewport-width': 900,   // Reduce width
		//   'viewport-height': 600,  // Reduce height
		};
	  
		// Convert parameters to a query string
		const queryString = Object.keys(params)
		  .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
		  .join('&');
	  
		// Return the full URL to your screenshot service.
		return `https://screenshot.dartegnian.com/take?${queryString}`;
	}

	async navigate(nav: "prev" | "next") {
		if (this.isEasterEggEnabled) {
			return;
		}

		switch (nav) {
			case "prev":
				if (this.day - 1 < 0) {
					if (this.month - 1 === -1) {
						// await this.triggerCalendarEasterEgg();
					} else {
						this.setActiveDate(this.month - 1, this.calendar.months[this.month - 1].days - 1)
					}
				} else {
					this.setActiveDate(this.month, this.day - 1)
				}
				break;
			case "next":
				if (this.day + 1 > this.calendar.months[this.month].days - 1) {
					if (this.month + 1 <= 11) {
						this.setActiveDate(this.month + 1, 0)
					}
				} else {
					this.setActiveDate(this.month, this.day + 1)
				}
				break;
			default:
				break;
		}
	}

	get prevLink(): any[] {
		let y = +this.year;
		let m = this.month;
		let d = this.day;

		if (d - 1 < 0) {
			if (m - 1 < 0) {
				y--; m = 11; d = this.calendar.months[11].days - 1;
			} else {
				m--; d = this.calendar.months[m].days - 1;
			}
		} else {
			d--;
		}

		const mm = String(m + 1).padStart(2, '0');
		const dd = String(d + 1).padStart(2, '0');
		return ['/calendar', y, mm, dd];
	}

	/** Build the correct routerLink for the next date */
	get nextLink(): any[] {
		let y = +this.year;
		let m = this.month;
		let d = this.day;

		if (d + 1 >= this.calendar.months[m].days) {
			if (m + 1 > 11) {
				y++; m = 0; d = 0;
			} else {
				m++; d = 0;
			}
		} else {
			d++;
		}

		const mm = String(m + 1).padStart(2, '0');
		const dd = String(d + 1).padStart(2, '0');
		return ['/calendar', y, mm, dd];
	}

	triggerInvalidDateEasterEgg(): void {
		this.isDateValid = true;
		this.isEasterEggEnabled = true;

		this.year = 2017;
		this.month = 8;
		this.day = 21;
		this.dateData = {
			status: "special",
			title: "Doki Doki Literature Club!",
			reason: "The Literature Club is full of cute girls! Will you write the way into their heart? This game is not suitable for children or those who are easily disturbed.",
			spotify: {
				link: "https://open.spotify.com/track/2OSFhiIBYXIBiVOyLnDQ2b?si=b1be57a931394cc7",
				artist: "Dan Salvato",
				song: "Doki Doki Literature Club!",
				album: "Doki Doki Literature Club! (Original Soundtrack)"
			},
			resonatedLyrics: [
				"Instrumental music"
			]
		}
	}
}
